import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { formatInstantDate } from '../../lib/format';
import { withNext } from '../../lib/nextPath';
import type { InvitePreviewRead } from '../../types/api';
import { useAccount } from '../onboarding/useAccount';
import { AuthCard } from './AuthCard';
import { acceptInvite, fetchInvitePreview } from './inviteApi';
import { useAuth } from './useAuth';

type Loaded =
  | { token: string; kind: 'preview'; preview: InvitePreviewRead }
  | { token: string; kind: 'not_found' }
  | { token: string; kind: 'error'; message: string };

/** What a refused accept means to the person holding the link. */
type AcceptOutcome =
  | { kind: 'mismatch' }
  | { kind: 'expired' }
  | { kind: 'accepted' }
  | { kind: 'error'; message: string };

function sameEmail(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Where an invite link lands: `/invite/:token`.
 *
 * Signed in, and deliberately outside the onboarding gate: someone invited to
 * a brand has nothing to onboard, and accepting is what gives them a brand.
 * Signed out, the route guard sends them to sign in (or sign up) with
 * `?next=` pointing back here.
 *
 * The invite is for one address. When the session is another one the page
 * says so before the click (the server refuses the accept with 403 anyway),
 * and offers to sign out and come back.
 */
export function InvitePage() {
  const { token = '' } = useParams<{ token: string }>();
  const { user, signOut } = useAuth();
  const { addBusiness, refresh } = useAccount();
  const navigate = useNavigate();

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [outcome, setOutcome] = useState<AcceptOutcome | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const preview = await fetchInvitePreview(token, controller.signal);
        if (cancelled) return;
        setLoaded({ token, kind: 'preview', preview });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setLoaded({ token, kind: 'not_found' });
        } else {
          setLoaded({
            token,
            kind: 'error',
            message: getErrorMessage(caught, 'Could not read this invite.'),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token]);

  const current = loaded !== null && loaded.token === token ? loaded : null;

  async function handleAccept() {
    setOutcome(null);
    setIsAccepting(true);
    try {
      const business = await acceptInvite(token);
      // Into the list and active at once, then a re-read so the list and the
      // onboarding gate hold the server's version of the account.
      addBusiness(business);
      refresh();
      void navigate('/', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'INVITE_EMAIL_MISMATCH') {
        setOutcome({ kind: 'mismatch' });
      } else if (caught instanceof ApiError && caught.code === 'INVITE_EXPIRED') {
        setOutcome({ kind: 'expired' });
      } else if (caught instanceof ApiError && caught.code === 'INVITE_ACCEPTED') {
        setOutcome({ kind: 'accepted' });
      } else {
        setOutcome({
          kind: 'error',
          message: getErrorMessage(caught, 'Could not accept the invite. Try again.'),
        });
      }
    } finally {
      setIsAccepting(false);
    }
  }

  async function handleSignOut() {
    setIsSigningOut(true);
    try {
      await signOut();
    } catch {
      // Signing out locally still happened; carry on to the sign-in screen.
    } finally {
      setIsSigningOut(false);
      void navigate(withNext('/login', `/invite/${token}`), { replace: true });
    }
  }

  const signOutLine = (
    <button
      className="link-button"
      type="button"
      disabled={isSigningOut}
      onClick={() => void handleSignOut()}
    >
      {isSigningOut ? 'Signing out' : 'Sign out'}
    </button>
  );

  const footer = user?.email ? (
    <span>
      Signed in as {user.email}. {signOutLine}
    </span>
  ) : null;

  if (current === null) {
    return (
      <AuthCard title="Opening your invite." description="One moment.">
        <p className="field__hint" role="status">
          Loading
        </p>
      </AuthCard>
    );
  }

  if (current.kind === 'not_found') {
    return (
      <InviteFrame
        title="This invite link is not valid."
        description="Check that the whole link was copied, or ask the brand owner for a new one."
        footer={footer}
      >
        <Link className="button button--block" to="/">
          Go to Autopostmate
        </Link>
      </InviteFrame>
    );
  }

  if (current.kind === 'error') {
    return (
      <InviteFrame title="Could not open the invite." description={current.message} footer={footer}>
        <button
          className="button button--block"
          type="button"
          onClick={() => window.location.reload()}
        >
          Try again
        </button>
      </InviteFrame>
    );
  }

  const { preview } = current;
  const inviter = preview.invited_by_email ?? 'the brand owner';
  const status =
    outcome?.kind === 'expired'
      ? 'expired'
      : outcome?.kind === 'accepted'
        ? 'accepted'
        : preview.status;

  if (status === 'expired') {
    return (
      <InviteFrame
        title="This invite has expired."
        description={`Invites work for 7 days. Ask ${inviter} to send a new link for ${preview.business_name}.`}
        footer={footer}
      >
        <Link className="button button--block" to="/">
          Go to Autopostmate
        </Link>
      </InviteFrame>
    );
  }

  if (status === 'accepted') {
    return (
      <InviteFrame
        title="This invite was already used."
        description={`If it was you, ${preview.business_name} is in your brand list. Otherwise ask ${inviter} for a new link.`}
        footer={footer}
      >
        <Link className="button button--primary button--block" to="/">
          Go to Autopostmate
        </Link>
      </InviteFrame>
    );
  }

  const isMismatch =
    outcome?.kind === 'mismatch' || (!!user?.email && !sameEmail(user.email, preview.email));

  return (
    <InviteFrame
      title={`You're invited to ${preview.business_name}.`}
      description={`${inviter} invited you to work on ${preview.business_name} in Autopostmate.`}
      footer={footer}
    >
      <dl className="invite-page__facts">
        <div className="invite-page__fact">
          <dt>Invited by</dt>
          <dd>{inviter}</dd>
        </div>
        <div className="invite-page__fact">
          <dt>Invite for</dt>
          <dd>{preview.email}</dd>
        </div>
        <div className="invite-page__fact">
          <dt>Expires</dt>
          <dd>{formatInstantDate(preview.expires_at)}</dd>
        </div>
      </dl>

      {isMismatch ? (
        <p className="form__error" role="alert">
          This invite was sent to {preview.email}. Sign in with that address. {signOutLine}
        </p>
      ) : (
        <>
          {outcome?.kind === 'error' ? (
            <p className="form__error" role="alert">
              {outcome.message}
            </p>
          ) : null}
          <button
            className="button button--primary button--block"
            type="button"
            disabled={isAccepting}
            onClick={() => void handleAccept()}
          >
            {isAccepting ? 'Accepting' : 'Accept'}
          </button>
        </>
      )}
    </InviteFrame>
  );
}

interface InviteFrameProps {
  title: string;
  description: string;
  footer: ReactNode;
  children: ReactNode;
}

/** The sign-in frame, with the invite's body as one block so the card's stagger stays even. */
function InviteFrame({ title, description, footer, children }: InviteFrameProps) {
  return (
    <AuthCard title={title} description={description} footer={footer ?? undefined}>
      <div className="invite-page">{children}</div>
    </AuthCard>
  );
}
